import React from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/components/auth/AuthProvider';
import { ProtectedRoute } from './components/routing/ProtectedRoute';
import withCleanup from './components/auth/withCleanup';

const LandingPage = React.lazy(() => import('@/pages/LandingPage'));
const Login = React.lazy(() => import('@/pages/Login'));
const AuthCallback = React.lazy(() => import('@/pages/AuthCallback'));
const ResetPassword = React.lazy(() => import('@/pages/ResetPassword'));
const AcceptInvite = React.lazy(() => import('@/pages/AcceptInvite'));
import Dashboard from '@/pages/Dashboard';
const Planning = React.lazy(() => import('@/pages/Planning'));
const Records = React.lazy(() => import('@/pages/Records'));
const PiggyBanks = React.lazy(() => import('@/pages/PiggyBanks'));
const Settings = React.lazy(() => import('@/pages/Settings'));
const AddIncome = React.lazy(() => import('@/pages/AddIncome'));
const AddExpense = React.lazy(() => import('@/pages/AddExpense'));
const Reports = React.lazy(() => import('@/pages/Reports'));
const Tracking = React.lazy(() => import('@/pages/Tracking'));
const ModalExamples = React.lazy(() => import('@/pages/ModalExamples'));

// Aplica o HOC de limpeza
const CleanLandingPage = withCleanup(LandingPage);
const CleanLogin = withCleanup(Login);

function RecoveryRedirectGate({ children }: { children: React.ReactNode }) {
  const location = useLocation();

  const hash = location.hash.startsWith('#') ? location.hash.slice(1) : location.hash;
  const hashParams = new URLSearchParams(hash);
  const type = hashParams.get('type');

  if (type === 'recovery') {
    return <Navigate to={`/reset-password${location.search}${location.hash}`} replace />;
  }

  return <>{children}</>;
}

function StripBufunfaPrefixRedirect() {
  const location = useLocation();
  const strippedPathname = location.pathname.replace(/^\/bufunfa(\/|$)/, '/');
  const targetPathname = strippedPathname === '' ? '/' : strippedPathname;
  return <Navigate to={`${targetPathname}${location.search}${location.hash}`} replace />;
}

function PrivateRoute({ children }: { children: React.ReactNode }) {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-600"></div>
      </div>
    );
  }
  
  return user ? <>{children}</> : <Navigate to="/login" replace />;
}

function PublicRoute({ children }: { children: React.ReactNode }) {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-600"></div>
      </div>
    );
  }
  
  return !user ? <>{children}</> : <Navigate to="/records" replace />;
}

export default function AppRoutes() {
  const { loading } = useAuth();
  
  return (
    <Routes>
      <Route 
        path="/login" 
        element={
          <PublicRoute>
            <RecoveryRedirectGate>
              <CleanLogin />
            </RecoveryRedirectGate>
          </PublicRoute>
        } 
      />
      <Route 
        path="/auth/callback" 
        element={
          <React.Suspense fallback={
            <div className="min-h-screen flex items-center justify-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-600"></div>
            </div>
          }>
            <AuthCallback />
          </React.Suspense>
        } 
      />
      <Route
        path="/reset-password"
        element={
          <React.Suspense fallback={
            <div className="min-h-screen flex items-center justify-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-600"></div>
            </div>
          }>
            <ResetPassword />
          </React.Suspense>
        }
      />
      <Route path="/accept-invite" element={<AcceptInvite />} />
      <Route
        path="/"
        element={
          loading ? (
            <div className="min-h-screen flex items-center justify-center">
              <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-emerald-500"></div>
            </div>
          ) : (
            <RecoveryRedirectGate>
              <CleanLandingPage />
            </RecoveryRedirectGate>
          )
        }
      />
      {/* Adicionar rota catch-all para redirecionar /bufunfa/ para / */}
      <Route
        path="/bufunfa/*"
        element={<StripBufunfaPrefixRedirect />}
      />
      <Route 
        path="/dashboard" 
        element={<ProtectedRoute><Dashboard /></ProtectedRoute>}
      />
      <Route 
        path="/planning"
        element={
          <PrivateRoute>
            <Planning />
          </PrivateRoute>
        }
      />
      <Route
        path="/records"
        element={
          <PrivateRoute>
            <ProtectedRoute requiredPage="/records">
              <Records />
            </ProtectedRoute>
          </PrivateRoute>
        }
      />
      <Route
        path="/piggy-banks"
        element={
          <PrivateRoute>
            <ProtectedRoute requiredPage="/piggy-banks">
              <PiggyBanks />
            </ProtectedRoute>
          </PrivateRoute>
        }
      />
      <Route
        path="/settings"
        element={
          <PrivateRoute>
            <ProtectedRoute requiredPage="/settings">
              <Settings />
            </ProtectedRoute>
          </PrivateRoute>
        }
      />
      <Route
        path="/add-income"
        element={
          <PrivateRoute>
            <ProtectedRoute requiredPage="/add-income">
              <AddIncome />
            </ProtectedRoute>
          </PrivateRoute>
        }
      />
      <Route
        path="/add-expense"
        element={
          <PrivateRoute>
            <ProtectedRoute requiredPage="/add-expense">
              <AddExpense />
            </ProtectedRoute>
          </PrivateRoute>
        }
      />
      <Route
        path="/reports"
        element={
          <PrivateRoute>
            <ProtectedRoute requiredPage="/reports">
              <Reports />
            </ProtectedRoute>
          </PrivateRoute>
        }
      />
      <Route
        path="/tracking"
        element={
          <PrivateRoute>
            <ProtectedRoute requiredPage="/tracking">
              <Tracking />
            </ProtectedRoute>
          </PrivateRoute>
        }
      />
      <Route
        path="/modal-examples"
        element={
          <PrivateRoute>
            <ProtectedRoute requiredPage="/modal-examples">
              <ModalExamples />
            </ProtectedRoute>
          </PrivateRoute>
        }
      />
    </Routes>
  );
}
